import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import styled from 'styled-components';

import FieldValueBlock from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/FieldValueBlock';
import {
  buildRule,
  generateOperatorOptions,
  getRightHandDisplay,
} from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/utils';
import CaseDataManagerSchemaSelect from 'dashboard/components/CaseDataManagerSchemaSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import {
  ConditionalRule,
  getConditionalRuleParts,
  JsonLogicValue,
  RuleValueConfigField,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import { FieldSchemaType } from 'lib/fields';
import { JsonSchema } from 'lib/json-schema';

const DEFAULT_LEFT_CONFIG: RuleValueConfigField = {
  type: RuleValueTypes.Field,
  value: { var: '' },
  paths: [''],
};

const EditBaseRule = styled('div')<{
  editable: boolean;
}>`
  display: grid;
  grid-template-columns: 39% 14% 39%;
  grid-column-gap: ${Spacing[2]};
  margin-top: ${Spacing[1]};
  margin-bottom: ${Spacing[1]};

  ${({ editable }) => editable && `grid-template-columns: 30% 18% 42% 8%`}
  ${({ editable }) => !editable && `grid-template-columns: 39% 14% 39%`}
`;

interface EditRuleProps {
  baseRule: ConditionalRule;
  jsonSchema: JsonSchema;
  updateRule: (rule: ConditionalRule) => void;
  deleteRule: () => void;
  enableFieldValueDropdown: boolean;
  operatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>>;
  editable: boolean;
  enableValidation: boolean;
  isValid: boolean;
}

const EditRule = ({
  baseRule,
  jsonSchema,
  operatorsMap,
  enableFieldValueDropdown,
  editable,
  enableValidation,
  isValid,
  updateRule,
  deleteRule,
}: EditRuleProps) => {
  const { operator, valueLeftConfig, valueRightConfig } = getConditionalRuleParts(baseRule);

  // By defaulting to Value, if the field/value dropdown is not enabled,
  // only the value dropdown is available
  const initialDropdownType = valueRightConfig ? valueRightConfig.type : RuleValueTypes.Value;
  const [dropdownType, setDropdownType] = useState(initialDropdownType);
  const leftConfig = valueLeftConfig || DEFAULT_LEFT_CONFIG;

  const schemaForFieldPath = useSchemaForFieldPath();
  const generateNewOperatorOptions = (leftValue: string) => {
    const { schema: rowSchema } = schemaForFieldPath(leftValue, jsonSchema);
    return generateOperatorOptions(rowSchema, operatorsMap, operator);
  };

  const [operatorOptions, setOperatorOptions] = useState(
    generateNewOperatorOptions(leftConfig.value.var),
  );
  const leftValue = leftConfig.value.var;
  const rightValue = getRightHandDisplay(valueRightConfig);

  const valid = !enableValidation || isValid;

  const isOperatorDropdownEnabled = operatorOptions?.length && leftValue && editable;

  const updateRuleWithLeftValue = (val: JsonLogicValue) => {
    if (!val) {
      val = '';
    }
    const newOperatorOptions = generateNewOperatorOptions(val.toString());

    let newRule;
    if (newOperatorOptions.length === 0) {
      newRule = buildRule(WorkflowOperatorKey.Equals, val, dropdownType);
    } else if (!operator || !newOperatorOptions.map((option) => option.value).includes(operator)) {
      newRule = buildRule(newOperatorOptions[0].value, val, dropdownType);
    } else {
      newRule = buildRule(operator, val, dropdownType);
    }

    updateRule(newRule);
    setOperatorOptions(newOperatorOptions);
  };

  const updateRuleWithOperator = (op: WorkflowOperatorKey) => {
    const newRule = buildRule(op, leftValue, dropdownType);
    updateRule(newRule);
  };

  const updateRuleWithRightValue = (val: string | string[]) => {
    if (typeof val !== 'boolean' && !val) {
      val = '';
    }
    const newRule = buildRule(operator, leftValue, dropdownType, val);
    updateRule(newRule);
  };

  return (
    <EditBaseRule editable={editable}>
      <div>
        <CaseDataManagerSchemaSelect
          id='edit-rule-input-possibilities__left-field'
          jsonSchema={jsonSchema}
          inputValue={leftValue}
          onValueChange={(val) => updateRuleWithLeftValue(val)}
          disabled={!editable}
          hasError={!valid && !leftValue}
          portal
        />
      </div>
      <div>
        <ReactSelect
          id='edit-rule-input-possibilities__operator'
          aria-label='Select operator'
          value={operator}
          options={operatorOptions ?? []}
          disabled={!isOperatorDropdownEnabled}
          onChange={(val) => updateRuleWithOperator(val)}
          hasError={editable && isOperatorDropdownEnabled && !operator && !valid ? true : false}
          portal
        />
      </div>
      <FieldValueBlock
        enableFieldValueDropdown={enableFieldValueDropdown}
        dropdownType={dropdownType}
        hasOperatorOptions={operatorOptions.length > 0}
        editable={editable}
        operator={operator}
        jsonSchema={jsonSchema}
        fieldSchema={schemaForFieldPath(leftValue, jsonSchema).schema}
        formattedRightValue={rightValue as string | string[]}
        valid={valid}
        setDropdownType={setDropdownType}
        updateRuleWithRightValue={updateRuleWithRightValue}
      />
      {editable && (
        <Button id='multi-input__value-remove' variant='basic' width='icon' onClick={deleteRule}>
          <TrashIcon />
        </Button>
      )}
    </EditBaseRule>
  );
};

export default EditRule;
